"use client";

import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";

const caveat = Caveat({
  subsets: ["latin"],
  weight: ["700"],
  variable: "--font-caveat",
});

/* ─────────────────────────────────────────────────────────────
   1. TEXTILE MANUFACTURING: EFFICIENT CLIENT COMMUNICATION SECTION
   Exact reproduction of Odoo's CRM Pipeline Kanban Board UI
   ───────────────────────────────────────────────────────────── */
export function TextileEfficientClientCommunicationSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* CENTERED SECTION HEADER WITH HANDWRITTEN TITLE & UNDERLINE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-12 text-center max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Efficient client </span>
            <span className="relative inline-block px-1">
              communication
              <svg
                className="absolute -bottom-2 left-0 w-full h-3 text-amber-400 overflow-visible"
                viewBox="0 0 100 12"
                fill="none"
              >
                <path
                  d="M 4 6 Q 50 1, 96 6"
                  stroke="#F59E0B"
                  strokeWidth="5"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
          </h2>

          {/* 2 Paragraph Columns Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left max-w-4xl mx-auto pt-2">
            <p className="text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">The CRM efficiently qualifies</strong> client needs, allowing the immediate creation of a project for samples and quotations.
            </p>
            <p className="text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">Allow customers to easily request a quote</strong> using predefined templates on your website.
            </p>
          </div>
        </motion.div>

        {/* CRM PIPELINE KANBAN BOARD MOCKUP CARD */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="w-full flex justify-center"
        >
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[10px] sm:text-[11px] w-full max-w-5xl p-4 sm:p-5 font-sans">
            
            {/* Top Navigation & Toolbar Bar */}
            <div className="border-b border-slate-100 pb-2 mb-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-600 font-medium">
                <div className="flex items-center gap-2">
                  <span className="text-[#00A09D] font-bold flex items-center gap-0.5">▼ CRM</span>
                  <span className="cursor-pointer hover:text-slate-900">Sales</span>
                  <span className="cursor-pointer hover:text-slate-900">Reporting</span>
                  <span className="cursor-pointer hover:text-slate-900">Configuration</span>
                </div>
                <div className="flex items-center gap-2 text-[9px] text-slate-500">
                  <span className="w-2 h-2 rounded-full bg-rose-500 inline-block" />
                  <span>🎓</span>
                  <span className="bg-purple-100 text-purple-700 px-1 rounded font-bold">AI</span>
                  <span>💬 <span className="bg-rose-500 text-white rounded-full text-[7px] px-1 font-bold">18</span></span>
                  <span>🔔 <span className="bg-rose-500 text-white rounded-full text-[7px] px-1 font-bold">42</span></span>
                  <span>✖</span>
                </div>
              </div>

              {/* Action Buttons & Pipeline Search */}
              <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
                <div className="flex items-center gap-1.5 text-[10px]">
                  <button className="bg-[#7C5A76] hover:bg-[#674861] text-white px-2 py-0.5 rounded font-semibold text-[9px] transition-colors">
                    New
                  </button>
                  <button className="bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-2 py-0.5 rounded font-semibold text-[9px] transition-colors">
                    Generate Leads
                  </button>
                  <span className="text-slate-600 font-semibold text-[10px] ml-1">Pipeline ⚙️</span>
                </div>

                <div className="flex items-center gap-1.5">
                  <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-[9px] text-slate-500">
                    <span>🔍</span>
                    <span className="bg-[#7C5A76] text-white px-1 rounded font-semibold text-[8px]">My Pipeline ✖</span>
                    <span>Search...</span>
                  </div>
                  <span className="bg-slate-100 border border-slate-200 rounded p-1 text-[9px] cursor-pointer">📊</span>
                </div>
              </div>
            </div>

            {/* Pipeline Kanban Board Columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              
              {/* Column 1: New ($80,000) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                  <span>New</span>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 font-normal">+</span>
                    <span className="text-slate-600 font-bold text-[9px]">$ 80,000</span>
                  </div>
                </div>
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div className="w-4/5 bg-emerald-500 h-full" />
                </div>

                {/* Card */}
                <div className="bg-white border border-slate-200 rounded-lg p-2 shadow-sm space-y-1.5 hover:shadow-md transition-shadow">
                  <strong className="text-slate-900 text-[10px] block font-bold">Quote for Corporate shirt</strong>
                  <p className="text-slate-500 text-[9px] font-semibold">$ 40,000.00</p>
                  <div className="flex items-center gap-1 text-[8px] text-slate-500">
                    <span className="text-emerald-600 font-semibold">🏢 OpenWood</span>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[7.5px] px-1 rounded font-semibold">Product</span>
                  </div>
                  <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[8px]">
                    <span className="text-amber-400">⭐ <span className="text-slate-300">☆☆</span> <span className="text-slate-400">🕒</span></span>
                    <img src="/odoo/unsplash/avatars/u3.jpg" alt="Agent" className="w-3.5 h-3.5 rounded-full object-cover" />
                  </div>
                </div>
              </div>

              {/* Column 2: Qualified ($51,300) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                  <span>Qualified</span>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 font-normal">+</span>
                    <span className="text-slate-600 font-bold text-[9px]">$ 51,300</span>
                  </div>
                </div>
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden flex">
                  <div className="w-2/3 bg-emerald-500 h-full" />
                  <div className="w-1/3 bg-amber-400 h-full" />
                </div>

                {/* Card */}
                <div className="bg-white border border-slate-200 rounded-lg p-2 shadow-sm space-y-1.5 hover:shadow-md transition-shadow">
                  <strong className="text-slate-900 text-[10px] block font-bold">New Summer design</strong>
                  <p className="text-slate-500 text-[9px] font-semibold">$ 3,800.00</p>
                  <div className="flex items-center gap-1 text-[8px] text-slate-500">
                    <span className="text-sky-600 font-semibold">👥 Ready Mat</span>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="bg-purple-50 text-purple-700 border border-purple-200 text-[7.5px] px-1 rounded font-semibold">Design</span>
                  </div>
                  <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[8px]">
                    <span className="text-amber-400">⭐⭐ <span className="text-slate-300">☆</span> <span className="text-emerald-600">📞</span></span>
                    <img src="/odoo/unsplash/avatars/u3.jpg" alt="Agent" className="w-3.5 h-3.5 rounded-full object-cover" />
                  </div>
                </div>
              </div>

              {/* Column 3: Proposition ($79,100) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                  <span>Proposition</span>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 font-normal">+</span>
                    <span className="text-slate-600 font-bold text-[9px]">$ 79,100</span>
                  </div>
                </div>
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden flex">
                  <div className="w-1/2 bg-amber-400 h-full" />
                  <div className="w-1/2 bg-rose-500 h-full" />
                </div>

                {/* Card with Pinkish tint */}
                <div className="bg-rose-50/50 border border-rose-200 rounded-lg p-2 shadow-sm space-y-1.5 hover:shadow-md transition-shadow">
                  <strong className="text-slate-900 text-[10px] block font-bold">Customized Sport Team Polo</strong>
                  <p className="text-slate-500 text-[9px] font-semibold">$ 5,600.00</p>
                  <div className="flex items-center gap-1 text-[8px] text-slate-500">
                    <span className="text-emerald-600 font-semibold">🏢 Azure Interior</span>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="bg-amber-50 text-amber-700 border border-amber-200 text-[7.5px] px-1 rounded font-semibold">Services</span>
                  </div>
                  <div className="flex items-center justify-between pt-1 border-t border-rose-100 text-[8px]">
                    <span className="text-amber-400">⭐⭐⭐ <span className="text-rose-500">✉️</span></span>
                    <div className="flex items-center gap-1">
                      <span className="bg-rose-500 text-white font-bold text-[7px] px-1 rounded-full">6d</span>
                      <img src="/odoo/unsplash/avatars/u3.jpg" alt="Agent" className="w-3.5 h-3.5 rounded-full object-cover" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 4: Won ($19,800) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                  <span>Won</span>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 font-normal">+</span>
                    <span className="text-slate-600 font-bold text-[9px]">$ 19,800</span>
                  </div>
                </div>
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div className="w-full bg-emerald-500 h-full" />
                </div>

                {/* Card */}
                <div className="bg-white border border-slate-200 rounded-lg p-2 shadow-sm space-y-1.5 hover:shadow-md transition-shadow">
                  <strong className="text-slate-900 text-[10px] block font-bold">Standard T-shirt</strong>
                  <p className="text-slate-500 text-[9px] font-semibold">$ 19,800.00</p>
                  <div className="flex items-center gap-1 text-[8px] text-slate-500">
                    <span className="text-sky-600 font-semibold">🏢 Gemini Furniture</span>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="bg-sky-50 text-sky-700 border border-sky-200 text-[7.5px] px-1 rounded font-semibold">Information</span>
                    <span className="bg-purple-50 text-purple-700 border border-purple-200 text-[7.5px] px-1 rounded font-semibold">Other</span>
                  </div>
                  <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-[8px]">
                    <span className="text-amber-400">⭐⭐ <span className="text-slate-300">☆</span> <span className="text-emerald-600">📞</span></span>
                    <img src="/odoo/unsplash/avatars/u3.jpg" alt="Agent" className="w-3.5 h-3.5 rounded-full object-cover" />
                  </div>
                </div>
              </div>

            </div>

          </div>
        </motion.div>

        {/* BOTTOM PERSONA SPEECH BUBBLE CALLOUT */}
        <div className="w-full flex justify-center mt-10 sm:mt-12">
          <div className="flex items-center justify-center gap-2 sm:gap-3 max-w-full px-2">
            <img
              className="rounded-full border border-white/20 shadow-lg flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 object-cover"
              alt="Odoo Specialist"
              src="/odoo/snippets/s_wd_persona/foto.webp"
            />
            <div className="bg-white/10 text-slate-800 rounded-full shadow-2xl p-2 sm:p-2.5 px-4 sm:px-6 border border-slate-100 text-center">
              <i className="text-white text-xs sm:text-sm font-semibold block leading-snug">
                Double the speed of your quotation process.
              </i>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   2. TEXTILE MANUFACTURING: STREAMLINED STOCK MANAGEMENT SECTION
   Exact reproduction of Odoo's Inventory Overview Dashboard & Barcode Scanner Traceability Screen UI
   ───────────────────────────────────────────────────────────── */
export function TextileStreamlinedStockManagementSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* CENTERED SECTION HEADER WITH HANDWRITTEN TITLE & DOUBLE WAVY GREEN UNDERLINE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-12 text-center max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span className="relative inline-block px-1">
              Streamlined
              <svg
                className="absolute -bottom-2 left-0 w-full h-3 text-emerald-400 overflow-visible"
                viewBox="0 0 100 12"
                fill="none"
              >
                <path
                  d="M 4 5 Q 25 1, 50 6 Q 75 11, 96 5"
                  stroke="#10B981"
                  strokeWidth="5"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
            <span> stock management</span>
          </h2>

          {/* 2 Subtext Paragraph Columns Grid Side-by-Side (Aligns above the 2 cards!) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 text-left max-w-5xl mx-auto pt-2 ">
            <div className="md:col-span-7">
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">Delivery orders</strong> are automatically created in the Inventory app as soon as quotations are confirmed.
              </p>
            </div>
            <div className="md:col-span-5">
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">Lots &amp; packages tracking</strong> allows full traceability of your inventory.
              </p>
            </div>
          </div>
        </motion.div>

        {/* 2 OVERLAPPING MOCKUP CARDS GRID (INVENTORY OVERVIEW + SCANNER TRACEABILITY) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 w-full max-w-5xl mx-auto items-start"
        >
          {/* LEFT CARD: INVENTORY OVERVIEW DASHBOARD (7 cols) */}
          <div className="lg:col-span-7 bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[10px] sm:text-[11px] p-3 sm:p-4 font-sans">
            
            {/* Top Bar */}
            <div className="border-b border-slate-100 pb-2 mb-3 space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-1 text-[9.5px] text-slate-600 font-medium">
                <div className="flex items-center gap-1.5">
                  <span className="text-[#00A09D] font-bold flex items-center gap-0.5">📦 Inventory</span>
                  <span className="cursor-pointer hover:text-slate-900">Overview</span>
                  <span className="cursor-pointer hover:text-slate-900">Operations</span>
                  <span className="cursor-pointer hover:text-slate-900">Products</span>
                  <span className="cursor-pointer hover:text-slate-900">Reporting</span>
                </div>
                <div className="flex items-center gap-1 text-[8.5px] text-slate-500">
                  <span>💬 3</span>
                  <span>🛠️</span>
                  <span className="font-bold text-slate-800">My Company</span>
                  <span className="w-4 h-4 rounded bg-rose-700 text-white font-bold text-[9px] flex items-center justify-center">A</span>
                </div>
              </div>

              {/* Sub Header */}
              <div className="flex flex-wrap items-center justify-between gap-1 pt-0.5">
                <span className="text-slate-700 font-bold text-[10px]">Inventory Overview ⚙️</span>
                <div className="flex items-center gap-2 text-[8.5px] text-slate-500">
                  <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                    <span>🔍</span>
                    <span>Search...</span>
                  </div>
                  <span>1-5 / 5 &lt; &gt;</span>
                </div>
              </div>
            </div>

            {/* 4 Inventory Dashboard Cards (2x2 Grid) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              
              {/* Card 1: Receipts */}
              <div className="border border-slate-200 rounded-xl p-2.5 space-y-2 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#00A09D] text-[10.5px]">Receipts</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="bg-[#6B4C63] text-white px-2 py-0.5 rounded font-bold text-[9px]">9 To Receive</span>
                  <div className="text-[8.5px] text-slate-500 text-right">
                    <p>Late <strong className="text-[#00A09D]">2</strong></p>
                    <p>Operations <strong className="text-[#00A09D]">11</strong></p>
                  </div>
                </div>
                {/* Colorful Bar Chart Graphic */}
                <div className="flex items-end gap-1.5 h-12 pt-2">
                  <div className="w-1/5 bg-rose-400 rounded-t h-4/5" />
                  <div className="w-1/5 bg-amber-400 rounded-t h-2/5" />
                  <div className="w-1/5 bg-emerald-400 rounded-t h-full" />
                  <div className="w-1/5 bg-emerald-300 rounded-t h-3/5" />
                  <div className="w-1/5 bg-emerald-400 rounded-t h-3/5" />
                </div>
              </div>

              {/* Card 2: Delivery Orders */}
              <div className="border border-slate-200 rounded-xl p-2.5 space-y-2 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#00A09D] text-[10.5px]">Delivery Orders</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="bg-[#6B4C63] text-white px-2 py-0.5 rounded font-bold text-[9px]">4 To Deliver</span>
                  <div className="text-[8.5px] text-slate-500 text-right">
                    <p>Waiting <strong className="text-[#00A09D]">4</strong></p>
                    <p>Late <strong className="text-[#00A09D]">1</strong></p>
                    <p>Operations <strong className="text-[#00A09D]">4</strong></p>
                  </div>
                </div>
                {/* Colorful Bar Chart Graphic */}
                <div className="flex items-end gap-1.5 h-12 pt-2">
                  <div className="w-1/5 bg-rose-400 rounded-t h-2/5" />
                  <div className="w-1/5 bg-amber-400 rounded-t h-4/5" />
                  <div className="w-1/5 bg-emerald-400 rounded-t h-3/5" />
                  <div className="w-1/5 bg-emerald-300 rounded-t h-3/5" />
                  <div className="w-1/5 bg-emerald-400 rounded-t h-3/5" />
                </div>
              </div>

              {/* Card 3: PoS Orders */}
              <div className="border border-slate-200 rounded-xl p-2.5 space-y-2 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#00A09D] text-[10.5px]">PoS Orders</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="bg-[#6B4C63] text-white px-2 py-0.5 rounded font-bold text-[9px]">8 To Deliver</span>
                  <div className="text-[8.5px] text-slate-500 text-right">
                    <p>Late <strong className="text-[#00A09D]">1</strong></p>
                    <p>Operations <strong className="text-[#00A09D]">8</strong></p>
                  </div>
                </div>
                {/* Colorful Bar Chart Graphic */}
                <div className="flex items-end gap-1.5 h-12 pt-2">
                  <div className="w-1/4 bg-rose-400 rounded-t h-1/5" />
                  <div className="w-1/4 bg-amber-400 rounded-t h-2/5" />
                  <div className="w-1/4 bg-emerald-400 rounded-t h-full" />
                  <div className="w-1/4 bg-emerald-300 rounded-t h-3/5" />
                </div>
              </div>

              {/* Card 4: Repairs */}
              <div className="border border-slate-200 rounded-xl p-2.5 space-y-2 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#00A09D] text-[10.5px]">Repairs</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="bg-[#6B4C63] text-white px-2 py-0.5 rounded font-bold text-[9px]">2 To Repair</span>
                  <div className="text-[8.5px] text-slate-500 text-right">
                    <p>Late <strong className="text-[#00A09D]">1</strong></p>
                    <p>Confirmed <strong className="text-[#00A09D]">3</strong></p>
                  </div>
                </div>
                {/* Colorful Bar Chart Graphic */}
                <div className="flex items-end gap-1.5 h-12 pt-2">
                  <div className="w-1/4 bg-rose-400 rounded-t h-1/5" />
                  <div className="w-1/4 bg-amber-400 rounded-t h-2/5" />
                  <div className="w-1/4 bg-emerald-400 rounded-t h-3/5" />
                  <div className="w-1/4 bg-emerald-300 rounded-t h-3/5" />
                </div>
              </div>

            </div>

          </div>

          {/* RIGHT CARD: BARCODE SCANNER INVENTORY TRACEABILITY SCREEN (5 cols) */}
          <div className="lg:col-span-5 bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[10px] sm:text-[11px] font-sans">
            
            {/* Top Dark Navy Header Scanner Bar */}
            <div className="bg-[#1C2536] text-white p-3 text-center space-y-1">
              <div className="flex items-center justify-center gap-1.5 text-xs font-semibold">
                <span>Scan a <strong className="underline decoration-sky-400">transfer</strong> or a <strong className="underline decoration-sky-400">product</strong> to filter your records</span>
                <span className="text-sky-400 text-sm">🔲</span>
              </div>
            </div>

            {/* Delivery Orders Traceability List */}
            <div className="divide-y divide-slate-100 p-2 sm:p-3 text-[10px]">
              
              {/* Item 1 */}
              <div className="py-2 flex items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-amber-400 font-bold">⭐</span>
                    <strong className="text-slate-900 font-bold text-[10.5px]">WH/OUT/00013</strong>
                  </div>
                  <span className="text-slate-400 text-[9px] block mt-0.5">Kelvin Admin</span>
                </div>
                <div className="text-right space-y-1">
                  <span className="bg-emerald-500 text-white font-bold text-[8.5px] px-2 py-0.5 rounded-full inline-block">Ready</span>
                  <div className="flex items-center gap-1 justify-end text-[8.5px] text-slate-400">
                    <span>11/18/2024</span>
                    <span className="w-3.5 h-3.5 rounded bg-rose-700 text-white font-bold text-[7.5px] flex items-center justify-center">A</span>
                  </div>
                </div>
              </div>

              {/* Item 2 */}
              <div className="py-2 flex items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-amber-400 font-bold">⭐</span>
                    <strong className="text-slate-900 font-bold text-[10.5px]">WH/OUT/00009</strong>
                  </div>
                  <span className="text-slate-400 text-[9px] block mt-0.5">Kelvin Admin</span>
                </div>
                <div className="text-right space-y-1">
                  <span className="bg-emerald-500 text-white font-bold text-[8.5px] px-2 py-0.5 rounded-full inline-block">Ready</span>
                  <div className="flex items-center gap-1 justify-end text-[8.5px] text-slate-400">
                    <span>11/19/2024</span>
                    <span className="w-3.5 h-3.5 rounded bg-rose-700 text-white font-bold text-[7.5px] flex items-center justify-center">A</span>
                  </div>
                </div>
              </div>

              {/* Item 3 */}
              <div className="py-2 flex items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-300 font-bold">☆</span>
                    <strong className="text-slate-900 font-bold text-[10.5px]">WH/OUT/00005</strong>
                  </div>
                  <span className="text-slate-400 text-[9px] block mt-0.5">Stephen</span>
                </div>
                <div className="text-right space-y-1">
                  <span className="bg-emerald-500 text-white font-bold text-[8.5px] px-2 py-0.5 rounded-full inline-block">Waiting</span>
                  <span className="text-slate-400 text-[8.5px] block">11/17/2024</span>
                </div>
              </div>

              {/* Item 4 */}
              <div className="py-2 flex items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-amber-400 font-bold">⭐</span>
                    <strong className="text-slate-900 font-bold text-[10.5px]">WH/OUT/00011</strong>
                  </div>
                  <span className="text-slate-400 text-[9px] block mt-0.5">Kelvin Admin</span>
                </div>
                <div className="text-right space-y-1">
                  <span className="bg-emerald-500 text-white font-bold text-[8.5px] px-2 py-0.5 rounded-full inline-block">Ready</span>
                  <div className="flex items-center gap-1 justify-end text-[8.5px] text-slate-400">
                    <span>11/18/2024</span>
                    <span className="w-3.5 h-3.5 rounded bg-rose-700 text-white font-bold text-[7.5px] flex items-center justify-center">A</span>
                  </div>
                </div>
              </div>

              {/* Item 5 */}
              <div className="py-2 flex items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-300 font-bold">☆</span>
                    <strong className="text-slate-900 font-bold text-[10.5px]">WH/OUT/00012</strong>
                  </div>
                  <span className="text-slate-400 text-[9px] block mt-0.5">Stephen</span>
                </div>
                <div className="text-right space-y-1">
                  <span className="bg-emerald-500 text-white font-bold text-[8.5px] px-2 py-0.5 rounded-full inline-block">Waiting</span>
                  <div className="flex items-center gap-1 justify-end text-[8.5px] text-slate-400">
                    <span>11/18/2024</span>
                    <span className="w-3.5 h-3.5 rounded bg-rose-700 text-white font-bold text-[7.5px] flex items-center justify-center">A</span>
                  </div>
                </div>
              </div>

              {/* Item 6 */}
              <div className="py-2 flex items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-300 font-bold">☆</span>
                    <strong className="text-slate-900 font-bold text-[10.5px]">WH/OUT/00014</strong>
                  </div>
                  <span className="text-slate-400 text-[9px] block mt-0.5">Stephen</span>
                </div>
                <div className="text-right space-y-1">
                  <span className="bg-emerald-500 text-white font-bold text-[8.5px] px-2 py-0.5 rounded-full inline-block">Waiting</span>
                  <div className="flex items-center gap-1 justify-end text-[8.5px] text-slate-400">
                    <span>11/20/2024</span>
                    <span className="w-3.5 h-3.5 rounded bg-rose-700 text-white font-bold text-[7.5px] flex items-center justify-center">A</span>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. TEXTILE MANUFACTURING: SMOOTH PRODUCTION WORKFLOW SECTION
   Exact reproduction of Odoo's Shopfloor App & Work Orders UI
   ───────────────────────────────────────────────────────────── */
export function TextileSmoothProductionWorkflowSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-4 lg:gap-6 items-center">

          {/* LEFT COLUMN: HANDWRITTEN TITLE & COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 space-y-4 text-left relative"
          >
            {/* Title with Coral Underline under "workflow" */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1] relative`}>
              <div>Smooth production</div>
              <div>
                <span className="relative inline-block px-1">
                  workflow
                  <span className="absolute bottom-1 left-0 w-full h-3 bg-[#FF6B6B] -z-10 rounded-full opacity-90" />
                </span>
              </div>
            </h2>

            {/* Subtext Copy */}
            <div className="space-y-3">
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">Create predefined or custom Bills of Material</strong> to automatically plan, procure, and manage components for your production orders.
              </p>
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">With the Odoo Shopfloor App,</strong> your workers can easily track and validate every production and quality step.
              </p>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: ODOO SHOPFLOOR APP MOCKUP CARD (Small & Precise max-w-[520px]) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 relative w-full flex justify-center lg:justify-end"
          >
            {/* White Window Container */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[9.5px] sm:text-[10.5px] w-full max-w-xl p-3 sm:p-4 font-sans">
              
              {/* Shopfloor Top Header Navigation */}
              <div className="border-b border-slate-100 pb-2 mb-2 space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-1 text-[9px] text-slate-600 font-medium">
                  <div className="flex items-center gap-1">
                    <span className="bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded text-[8.5px]">Overview <strong className="ml-0.5">6</strong></span>
                    <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[8.5px]">My WO <strong className="ml-0.5">1</strong></span>
                    <span className="bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded text-[8.5px]">Line 1 <strong className="ml-0.5">4</strong></span>
                    <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[8.5px]">Line 2 <strong className="ml-0.5">1</strong></span>
                    <span className="text-slate-400 font-normal px-1">+ Add</span>
                  </div>
                  <div className="flex items-center gap-1 text-[8px] text-slate-500">
                    <span>🔍</span>
                    <span className="bg-purple-100 text-purple-800 px-1 rounded font-semibold text-[7.5px]">WO Ready ✖</span>
                  </div>
                </div>
              </div>

              {/* Shopfloor Main Interface Split (Workers Sidebar + Work Orders Grid) */}
              <div className="grid grid-cols-12 gap-2">
                
                {/* Workers Sidebar (4 cols) */}
                <div className="col-span-4 border-r border-slate-100 pr-1.5 space-y-1.5 text-[8.5px]">
                  
                  {/* Worker 1 */}
                  <div className="bg-slate-50 border border-slate-200 rounded p-1 space-y-0.5">
                    <div className="flex items-center gap-1">
                      <img src="/odoo/unsplash/avatars/u3.jpg" alt="Aka Foster" className="w-3.5 h-3.5 rounded-full object-cover" />
                      <strong className="text-slate-900 text-[8.5px] truncate">Aka Foster</strong>
                    </div>
                    <div className="flex items-center justify-between text-[7.5px]">
                      <span className="bg-amber-100 text-amber-800 px-1 rounded font-semibold">Line 2</span>
                      <span className="text-sky-600 font-bold">0'</span>
                    </div>
                  </div>

                  {/* Worker 2 */}
                  <div className="bg-slate-50 border border-slate-200 rounded p-1 space-y-0.5">
                    <div className="flex items-center gap-1">
                      <img src="/odoo/unsplash/avatars/u3.jpg" alt="Mitchell Admin" className="w-3.5 h-3.5 rounded-full object-cover" />
                      <strong className="text-slate-900 text-[8.5px] truncate">Mitchell Admin</strong>
                    </div>
                    <div className="flex items-center justify-between text-[7.5px]">
                      <span className="bg-rose-100 text-rose-800 px-1 rounded font-semibold">Line 1</span>
                      <span className="text-sky-600 font-bold">1'</span>
                    </div>
                  </div>

                  {/* Worker 3 */}
                  <div className="p-0.5 text-slate-400 flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-slate-200 block" />
                    <span className="text-[8px]">Amy Watson</span>
                  </div>

                  {/* Worker 4 */}
                  <div className="p-0.5 text-slate-400 flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-slate-200 block" />
                    <span className="text-[8px]">Anita Oliver</span>
                  </div>

                  {/* Edit Operators Button */}
                  <button className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-1 rounded text-[7.5px] transition-colors mt-2">
                    Edit Operators
                  </button>
                </div>

                {/* Work Orders Grid (8 cols: 2 columns x 3 rows) */}
                <div className="col-span-8 grid grid-cols-2 gap-1.5 text-[8px]">
                  
                  {/* WO 1 */}
                  <div className="bg-sky-50/50 border border-sky-100 rounded p-1.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <strong className="text-slate-900 font-bold text-[8.5px]">WH/MO/00003</strong>
                      <span className="text-slate-500 font-semibold">1 Units</span>
                    </div>
                    <p className="text-slate-600 text-[7.5px]">Table Top</p>
                    <div className="bg-rose-100 text-rose-800 px-1 py-0.5 rounded font-semibold flex items-center justify-between text-[7px]">
                      <span>Line 1 Assembly</span>
                      <span>&gt;</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 text-[7px]">
                      <span className="bg-slate-200 text-slate-700 px-1 rounded font-bold">Close</span>
                      <span className="text-slate-400">!</span>
                    </div>
                  </div>

                  {/* WO 2 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <strong className="text-slate-900 font-bold text-[8.5px]">WH/MO/00007</strong>
                      <span className="text-emerald-600 font-bold text-[7px]">Confirmed</span>
                    </div>
                    <p className="text-slate-600 text-[7.5px]">Drawer</p>
                    <span className="text-slate-500 font-semibold block text-[7.5px]">5 Units</span>
                    <div className="flex items-center justify-between pt-0.5 text-[7px]">
                      <span className="bg-slate-200 text-slate-700 px-1 rounded font-bold">Close</span>
                      <span className="text-slate-400">!</span>
                    </div>
                  </div>

                  {/* WO 3 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <strong className="text-slate-900 font-bold text-[8.5px]">WH/MO/00010</strong>
                      <span className="text-slate-500 font-semibold">300 Units</span>
                    </div>
                    <p className="text-slate-600 text-[7.5px]">Polo</p>
                    <div className="bg-amber-100 text-amber-800 px-1 py-0.5 rounded font-semibold flex items-center justify-between text-[7px]">
                      <span>Line 2 Thread Work</span>
                      <span>&gt;</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 text-[7px]">
                      <span className="bg-slate-200 text-slate-700 px-1 rounded font-bold">Close</span>
                      <span className="text-slate-400">!</span>
                    </div>
                  </div>

                  {/* WO 4 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <strong className="text-slate-900 font-bold text-[8.5px]">WH/MO/00012</strong>
                      <span className="text-emerald-600 font-bold text-[7px]">Confirmed</span>
                    </div>
                    <p className="text-slate-600 text-[7.5px]">Corporate T-shirt</p>
                    <span className="text-slate-500 font-semibold block text-[7.5px]">250 Units</span>
                    <div className="bg-rose-100 text-rose-800 px-1 py-0.5 rounded font-semibold flex items-center justify-between text-[7px]">
                      <span>Line 1 Thread Work</span>
                      <span>&gt;</span>
                    </div>
                  </div>

                  {/* WO 5 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <strong className="text-slate-900 font-bold text-[8.5px]">WH/MO/00013</strong>
                      <span className="text-emerald-600 font-bold text-[7px]">Confirmed</span>
                    </div>
                    <p className="text-slate-600 text-[7.5px]">Blue Denim Jeans (2XL)</p>
                    <span className="text-slate-500 font-semibold block text-[7.5px]">30 Units</span>
                    <div className="bg-rose-100 text-rose-800 px-1 py-0.5 rounded font-semibold flex items-center justify-between text-[7px]">
                      <span>Line 1 Thread Work</span>
                      <span>&gt;</span>
                    </div>
                  </div>

                  {/* WO 6 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <strong className="text-slate-900 font-bold text-[8.5px]">WH/MO/00011</strong>
                      <span className="text-emerald-600 font-bold text-[7px]">Confirmed</span>
                    </div>
                    <p className="text-slate-600 text-[7.5px]">Blue Denim Jeans (S)</p>
                    <span className="text-slate-500 font-semibold block text-[7.5px]">40 Units</span>
                    <div className="bg-rose-100 text-rose-800 px-1 py-0.5 rounded font-semibold flex items-center justify-between text-[7px]">
                      <span>Line 1 Thread Work</span>
                      <span>&gt;</span>
                    </div>
                  </div>

                </div>

              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   4. TEXTILE MANUFACTURING: TRACK YOUR PURCHASE SECTION
   Exact reproduction of Odoo's Purchase Dashboard & RFQ Table UI
   ───────────────────────────────────────────────────────────── */
export function TextileTrackYourPurchaseSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* CENTERED SECTION HEADER WITH HANDWRITTEN TITLE & SKY BLUE UNDERLINE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-12 text-center max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Track your </span>
            <span className="relative inline-block px-1">
              purchase
              <span className="absolute bottom-1 left-0 w-full h-3 bg-sky-400 -z-10 rounded-full opacity-90" />
            </span>
          </h2>

          {/* Subtext Copywriting */}
          <p className="text-xl text-gray-300 leading-relaxed ">
            <strong className="text-white font-bold">Handle all purchasing, dropshipping, and subcontracting</strong> to keep your inventory perfectly stocked.
          </p>
        </motion.div>

        {/* PURCHASE DASHBOARD & RFQ TABLE MOCKUP CARD (Small & Precise max-w-[720px]) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="w-full flex items-center justify-center"
        >
          <div className="relative w-full max-w-5xl">
            {/* White Card Container */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[9.5px] sm:text-[10.5px] w-full p-3 sm:p-4 font-sans">
              
              {/* Top KPI Metrics Header Bar */}
              <div className="grid grid-cols-12 gap-1.5 border-b border-slate-100 pb-3 mb-3 text-[8.5px]">
                
                {/* Filter KPI Pills */}
                <div className="col-span-8 grid grid-cols-5 gap-1 text-center font-semibold">
                  <div className="bg-sky-100 text-sky-900 rounded p-1.5 space-y-0.5">
                    <strong className="text-xs block font-bold">10</strong>
                    <span className="text-[7.5px] text-sky-700">New</span>
                  </div>
                  <div className="bg-slate-100 text-slate-700 rounded p-1.5 space-y-0.5">
                    <strong className="text-xs block font-bold">1</strong>
                    <span className="text-[7.5px] text-slate-500">RFQ Sent</span>
                  </div>
                  <div className="bg-amber-100 text-amber-900 rounded p-1.5 space-y-0.5">
                    <strong className="text-xs block font-bold">11</strong>
                    <span className="text-[7.5px] text-amber-700">Late RFQ</span>
                  </div>
                  <div className="bg-emerald-100 text-emerald-900 rounded p-1.5 space-y-0.5">
                    <strong className="text-xs block font-bold">9</strong>
                    <span className="text-[7.5px] text-emerald-700">Not Acknowledged</span>
                  </div>
                  <div className="bg-rose-100 text-rose-900 rounded p-1.5 space-y-0.5">
                    <strong className="text-xs block font-bold">6</strong>
                    <span className="text-[7.5px] text-rose-700">Late Receipt</span>
                  </div>
                </div>

                {/* OTD & Lead Time Stats */}
                <div className="col-span-4 grid grid-cols-2 gap-1 text-center border-l border-slate-100 pl-2">
                  <div className="p-1 space-y-0.5">
                    <strong className="text-xs block font-bold text-slate-800">22 %</strong>
                    <span className="text-[7.5px] text-slate-400 font-semibold block">OTD</span>
                    <span className="text-[7.5px] text-slate-400">0%</span>
                  </div>
                  <div className="p-1 space-y-0.5">
                    <strong className="text-xs block font-bold text-slate-800">3.50</strong>
                    <span className="text-[7.5px] text-slate-400 font-semibold block">Days to Order</span>
                    <span className="text-[7.5px] text-slate-400">4.67</span>
                  </div>
                </div>

              </div>

              {/* Purchase Orders Data Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[8.5px] border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400 font-semibold text-[8px]">
                      <th className="pb-1.5 px-1">Reference</th>
                      <th className="pb-1.5 px-1">Vendor</th>
                      <th className="pb-1.5 px-1">Company</th>
                      <th className="pb-1.5 px-1">Buyer</th>
                      <th className="pb-1.5 px-1">Order Deadline</th>
                      <th className="pb-1.5 px-1">Activities</th>
                      <th className="pb-1.5 px-1 text-right">Total</th>
                      <th className="pb-1.5 px-1 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 text-slate-700">
                    {/* Row 1 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1.5 px-1 font-bold text-sky-600">P00020</td>
                      <td className="py-1.5 px-1 text-slate-600">Wood Corner</td>
                      <td className="py-1.5 px-1 text-sky-600 font-medium">My Company (San Francisco)</td>
                      <td className="py-1.5 px-1" />
                      <td className="py-1.5 px-1 text-rose-500 font-bold">3 days ago</td>
                      <td className="py-1.5 px-1">🕒</td>
                      <td className="py-1.5 px-1 text-right font-semibold">$ 862.50</td>
                      <td className="py-1.5 px-1 text-right"><span className="bg-sky-500 text-white font-bold text-[7.5px] px-1.5 py-0.2 rounded-full">RFQ</span></td>
                    </tr>

                    {/* Row 2 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1.5 px-1 font-bold text-sky-600">P00019</td>
                      <td className="py-1.5 px-1 text-slate-600">Wood Corner</td>
                      <td className="py-1.5 px-1 text-slate-500">My Company (San Francisco)</td>
                      <td className="py-1.5 px-1" />
                      <td className="py-1.5 px-1" />
                      <td className="py-1.5 px-1 text-emerald-600 font-semibold">✔️ Send shipping details</td>
                      <td className="py-1.5 px-1 text-right font-semibold">$ 46.00</td>
                      <td className="py-1.5 px-1 text-right"><span className="bg-emerald-500 text-white font-bold text-[7.5px] px-1.5 py-0.2 rounded-full">Purchase Order</span></td>
                    </tr>

                    {/* Row 3 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1.5 px-1 font-bold text-sky-600">P00018</td>
                      <td className="py-1.5 px-1 text-slate-600">Wood Corner</td>
                      <td className="py-1.5 px-1 text-slate-500">My Company (San Francisco)</td>
                      <td className="py-1.5 px-1" />
                      <td className="py-1.5 px-1" />
                      <td className="py-1.5 px-1">🕒</td>
                      <td className="py-1.5 px-1 text-right font-semibold">$ 276.00</td>
                      <td className="py-1.5 px-1 text-right"><span className="bg-emerald-500 text-white font-bold text-[7.5px] px-1.5 py-0.2 rounded-full">Purchase Order</span></td>
                    </tr>

                    {/* Row 4 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1.5 px-1 font-bold text-sky-600">P00017</td>
                      <td className="py-1.5 px-1 text-slate-600">YourCompany, Joel Willis</td>
                      <td className="py-1.5 px-1 text-slate-500">My Company (San Francisco)</td>
                      <td className="py-1.5 px-1 flex items-center gap-1"><img src="/odoo/unsplash/avatars/u3.jpg" alt="Mitchell Admin" className="w-3 h-3 rounded-full" /> <span className="text-slate-600">Mitchell Admin</span></td>
                      <td className="py-1.5 px-1" />
                      <td className="py-1.5 px-1">🕒</td>
                      <td className="py-1.5 px-1 text-right font-semibold">$ 57.50</td>
                      <td className="py-1.5 px-1 text-right"><span className="bg-emerald-500 text-white font-bold text-[7.5px] px-1.5 py-0.2 rounded-full">Purchase Order</span></td>
                    </tr>

                    {/* Row 5 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1.5 px-1 font-bold text-sky-600">P00016</td>
                      <td className="py-1.5 px-1 text-slate-600">Ready Mat</td>
                      <td className="py-1.5 px-1 text-slate-500">My Company (San Francisco)</td>
                      <td className="py-1.5 px-1 flex items-center gap-1"><img src="/odoo/unsplash/avatars/u3.jpg" alt="Mitchell Admin" className="w-3 h-3 rounded-full" /> <span className="text-slate-600">Mitchell Admin</span></td>
                      <td className="py-1.5 px-1" />
                      <td className="py-1.5 px-1">🕒</td>
                      <td className="py-1.5 px-1 text-right font-semibold">$ 7,976.40</td>
                      <td className="py-1.5 px-1 text-right"><span className="bg-emerald-500 text-white font-bold text-[7.5px] px-1.5 py-0.2 rounded-full">Purchase Order</span></td>
                    </tr>

                    {/* Row 6 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1.5 px-1 font-bold text-sky-600">P00015</td>
                      <td className="py-1.5 px-1 text-sky-600 font-medium">Deco Addict</td>
                      <td className="py-1.5 px-1 text-sky-600 font-medium">My Company (San Francisco)</td>
                      <td className="py-1.5 px-1 flex items-center gap-1"><img src="/odoo/unsplash/avatars/u3.jpg" alt="Mitchell Admin" className="w-3 h-3 rounded-full" /> <span className="text-sky-600 font-medium">Mitchell Admin</span></td>
                      <td className="py-1.5 px-1 text-amber-600 font-bold">Today</td>
                      <td className="py-1.5 px-1">🕒</td>
                      <td className="py-1.5 px-1 text-right font-semibold">$ 552.00</td>
                      <td className="py-1.5 px-1 text-right"><span className="bg-sky-500 text-white font-bold text-[7.5px] px-1.5 py-0.2 rounded-full">RFQ</span></td>
                    </tr>

                    {/* Row 7 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1.5 px-1 font-bold text-sky-600">P00014</td>
                      <td className="py-1.5 px-1 text-sky-600 font-medium">Wood Corner</td>
                      <td className="py-1.5 px-1 text-sky-600 font-medium">My Company (San Francisco)</td>
                      <td className="py-1.5 px-1 flex items-center gap-1"><img src="/odoo/unsplash/avatars/u3.jpg" alt="Mitchell Admin" className="w-3 h-3 rounded-full" /> <span className="text-sky-600 font-medium">Mitchell Admin</span></td>
                      <td className="py-1.5 px-1 text-amber-600 font-bold">Today</td>
                      <td className="py-1.5 px-1">🕒</td>
                      <td className="py-1.5 px-1 text-right font-semibold">$ 575.00</td>
                      <td className="py-1.5 px-1 text-right"><span className="bg-sky-500 text-white font-bold text-[7.5px] px-1.5 py-0.2 rounded-full">RFQ</span></td>
                    </tr>

                    {/* Row 8 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1.5 px-1 font-bold text-sky-600">P00013</td>
                      <td className="py-1.5 px-1 text-sky-600 font-medium">Wood Corner</td>
                      <td className="py-1.5 px-1 text-sky-600 font-medium">My Company (San Francisco)</td>
                      <td className="py-1.5 px-1 flex items-center gap-1"><img src="/odoo/unsplash/avatars/u3.jpg" alt="Mitchell Admin" className="w-3 h-3 rounded-full" /> <span className="text-sky-600 font-medium">Mitchell Admin</span></td>
                      <td className="py-1.5 px-1 text-amber-600 font-bold">Today</td>
                      <td className="py-1.5 px-1">🕒</td>
                      <td className="py-1.5 px-1 text-right font-semibold">$ 690.00</td>
                      <td className="py-1.5 px-1 text-right"><span className="bg-sky-500 text-white font-bold text-[7.5px] px-1.5 py-0.2 rounded-full">RFQ</span></td>
                    </tr>

                    {/* Row 9 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1.5 px-1 font-bold text-sky-600">P00012</td>
                      <td className="py-1.5 px-1 text-slate-600">Wood Corner</td>
                      <td className="py-1.5 px-1 text-slate-500">My Company (San Francisco)</td>
                      <td className="py-1.5 px-1 flex items-center gap-1"><img src="/odoo/unsplash/avatars/u3.jpg" alt="Mitchell Admin" className="w-3 h-3 rounded-full" /> <span className="text-slate-600">Mitchell Admin</span></td>
                      <td className="py-1.5 px-1" />
                      <td className="py-1.5 px-1">🕒</td>
                      <td className="py-1.5 px-1 text-right font-semibold">$ 1,725.00</td>
                      <td className="py-1.5 px-1 text-right"><span className="bg-emerald-500 text-white font-bold text-[7.5px] px-1.5 py-0.2 rounded-full">Purchase Order</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>

            {/* Right Decorative Sock Accent Icon (Directly beside the right edge of the card!) */}
            <div className="hidden lg:block absolute -right-12 bottom-6 z-20 text-sky-400 text-4xl sm:text-5xl transform rotate-12 filter drop-shadow-md">
              🧦
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}


